import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Shield, Users, Award, Clock, Star, CheckCircle, Handshake } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const values = [
  { icon: Shield, label: "Zertifiziert", desc: "Gepr\u00fcfte Qualit\u00e4t nach h\u00f6chsten Standards" },
  { icon: Users, label: "Erfahren", desc: "Qualifiziertes Fachpersonal in allen Bereichen" },
  { icon: Clock, label: "Zuverl\u00e4ssig", desc: "P\u00fcnktlichkeit und Termintreue garantiert" },
  { icon: Award, label: "Qualit\u00e4t", desc: "Hochwertige Materialien und Verarbeitung" },
];

const stats = [
  { value: "25+", label: "Jahre Erfahrung", icon: Star },
  { value: "500+", label: "Projekte abgeschlossen", icon: CheckCircle },
  { value: "100%", label: "Kundenzufriedenheit", icon: Handshake },
];

export const UeberUnsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        {/* Compact layout - image smaller, content side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 mb-16">
          {/* Left: Image - smaller, 2/5 */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 relative min-h-[300px] lg:min-h-[450px]"
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(/images/blueprints.jpg)` }}
            />
            <div className="absolute inset-0 bg-background/20" />
            {/* Overlay badge */}
            <div className="absolute bottom-0 right-0 bg-card border border-border p-5">
              <p className="font-heading font-bold text-3xl text-foreground">100%</p>
              <p className="font-heading font-semibold text-xs text-foreground mt-1">Kundenzufriedenheit</p>
              <div className="flex gap-0.5 mt-2">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-3 h-3 fill-brand-orange text-brand-orange" />
                ))}
              </div>
            </div>
            <div className="absolute top-0 left-0 w-1.5 h-full gradient-blue" />
          </motion.div>

          {/* Right: Content - 3/5 */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3 bg-background p-8 md:p-10 lg:p-12 flex flex-col justify-center"
          >
            <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
              {"\u00dcber die Freitag-Gruppe"}
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-4 leading-tight">
              Experten unter einem Dach.
            </h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed mb-6">
              {"Die Freitag-Gruppe vereint drei Fachunternehmen zu einer starken Einheit. Von der ersten Beratung \u00fcber die Planung bis zur finalen Umsetzung \u2013 wir begleiten Sie in jeder Phase."}
            </p>

            <div className="flex gap-0.5 mb-6">
              <div className="w-12 h-1 gradient-blue" />
              <div className="w-12 h-1 gradient-orange" />
              <div className="w-12 h-1 gradient-green" />
            </div>

            {/* Trust badges in 2x2 grid */}
            <div className="grid grid-cols-2 gap-3">
              {values.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="flex items-start gap-3 p-3 bg-card border border-border">
                    <div className="p-1.5 gradient-blue-subtle border border-brand-blue/20 shrink-0">
                      <Icon className="w-3.5 h-3.5 text-brand-blue-light" />
                    </div>
                    <div>
                      <p className="font-heading font-semibold text-xs text-foreground">{item.label}</p>
                      <p className="text-[10px] text-muted-foreground font-body">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border"
        >
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="border-0 bg-background">
                <CardContent className="p-6 text-center">
                  <Icon className="w-5 h-5 text-muted-foreground mx-auto mb-3" />
                  <p className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-1">{stat.value}</p>
                  <p className="text-xs text-muted-foreground font-body">{stat.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
